'use client'

import { useRef, useState, useTransition } from "react"
import { useRouter } from "next/navigation"
import { motion, AnimatePresence, useReducedMotion } from 'motion/react'
import { ArrowRight, Check, CircleSlash, RotateCcw, Search, ShoppingCart, Sparkles, TriangleAlert } from "lucide-react"

import { Button } from "@/components/elements/Button"
import AiSuggestions from "./AiSuggestions"
import AiOptions, { type ToneId, type LengthId } from "./AiOptions"
import TldResultList from "./TldResultList"
import { checkDomainAvailability, searchDomains, suggestDomainNames, type DomainSuggestion, type DomainSearchResult } from "@/lib/request"
import { useDomainCart, periodItems, CART_MAX_ITEMS, checkoutBlocker } from "@/hooks/use-domain-cart"
import Select from "@/components/elements/Select"
import CartPanel from "@/components/cart/CartPanel"
import { toast } from "@/lib/toast"
import { formatPrice } from "@/lib/util"

/**
 * The one thing this page does: say whether a `.mn` name is free.
 *
 * A display headline over a capsule search field, with the annual price stated
 * up front rather than discovered after a lookup - someone deciding whether to
 * bother should not have to search first to learn what it costs.
 *
 * `.mn` lives inside the capsule as a fixed anchor you cannot type over, because
 * this registry sells exactly one TLD. That is the whole difference between this
 * and a generic domain search, so it is the detail the design leans on.
 *
 * The backdrop carries the verdict (see `glow` below), which keeps the answer
 * from being a small card in a large empty page.
 */

/** Shown when empty. Latin on purpose - the field rejects anything else, so a
 *  Cyrillic placeholder would demonstrate an impossible value. */
const PLACEHOLDER = 'example'

/** Registry rules: letters, digits and inner hyphens, 3-63 characters. */
const VALID = /^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/

type Mode = 'search' | 'ai'

/** Mirrors MIN_DESCRIPTION / MAX_DESCRIPTION in the backend service. Checked
 *  here too so a description that cannot succeed never costs a round trip. */
const IDEA_MIN = 10
const IDEA_MAX = 500

type Result =
  | { kind: 'available', domain: string, price?: number, sale?: number, allowedYears?: number[] | null }
  | { kind: 'taken', domain: string }
  | { kind: 'error', message: string }

export default function DomainSearch({ signedIn, danVerified = false, price, featured = [] }: { signedIn: boolean, danVerified?: boolean, price?: number | null, featured?: { tld: string, price: number }[] }) {
  const [value, setValue] = useState('')
  const [result, setResult] = useState<Result | null>(null)
  const [hint, setHint] = useState<string | null>(null)
  const [pending, startTransition] = useTransition()
  // Its own transition, separate from the search above: `router.push` to
  // /checkout is a server render, so without this the button sits inert for the
  // whole round trip and reads as a dead click.
  const [navPending, startNavTransition] = useTransition()
  const inputRef = useRef<HTMLInputElement>(null)
  const reduceMotion = useReducedMotion()
  const cart = useDomainCart()
  const router = useRouter()
  // The term for the name currently on screen, before it is bought or collected.
  const [resultPeriod, setResultPeriod] = useState(1)
  // Every TLD besides `.mn` that came back with the last search. Kept apart from
  // `result` so it can never touch the verdict or the backdrop glow - see `glow`
  // below, which reads only `.mn`.
  const [otherResults, setOtherResults] = useState<DomainSearchResult[]>([])
  // The keyword the results on screen belong to. Kept apart from `value`, which
  // is whatever is in the field right now — changing the term must re-price the
  // name that was actually searched, not a half-typed new one.
  const [searchedName, setSearchedName] = useState('')

  // Two ways to ask the same question: type the name you already have in mind,
  // or describe the business and let the model propose some. Separate state on
  // purpose - switching back to the field should still show the verdict it left
  // behind, not a cleared page.
  const [mode, setMode] = useState<Mode>('search')
  const [idea, setIdea] = useState('')
  const [aiPending, startAiTransition] = useTransition()
  const [ai, setAi] = useState<{ suggestions: DomainSuggestion[], price?: number | null } | null>(null)
  const [aiHint, setAiHint] = useState<string | null>(null)
  const ideaRef = useRef<HTMLTextAreaElement>(null)
  // Both optional. Null means the model keeps its own spread, which is what an
  // untouched form asks for.
  const [tone, setTone] = useState<ToneId | null>(null)
  const [length, setLength] = useState<LengthId | null>(null)

  // Same easing the auth showcase uses, so verdicts here and there feel alike.
  // Reduced motion keeps the swap but drops the travel and shortens it.
  const cardMotion = {
    initial: { opacity: 0, y: reduceMotion ? 0 : 8 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: reduceMotion ? 0 : -6 },
    transition: { duration: reduceMotion ? 0.08 : 0.18, ease: 'easeOut' },
  } as const

  // Typed characters are normalised as they arrive: domain names have no
  // uppercase and no spaces, so silently correcting beats rejecting afterwards.
  const onChange = (raw: string) => {
    const next = raw.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9-]/g, '')
    setValue(next)
    if(result) setResult(null)
    if(hint) setHint(null)
    if(otherResults.length) setOtherResults([])
  }

  // The taken card's way out: empty the field and put the caret back in it, so
  // the next attempt costs one click instead of a select-all.
  const reset = () => {
    setValue('')
    setResult(null)
    setHint(null)
    setOtherResults([])
    inputRef.current?.focus()
  }

  // One handler, because the card offers adding from more than one place and
  // the full-basket refusal has to read identically wherever it happens.
  const addToCart = (domain: string) => {
    const allowedYears = result?.kind === 'available' ? (result.allowedYears ?? null) : null
    const res = cart.add(domain, resultPeriod, allowedYears)
    if(!res.ok && res.reason === 'full') return toast.error(`Сагсанд хамгийн ихдээ ${CART_MAX_ITEMS} домэйн нэр багтана`)
    toast.success('Сагсанд нэмэгдлээ')
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const name = value.trim()

    if(!name) return setHint('Шалгах нэрээ бичнэ үү.')
    if(name.length < 3) return setHint('Хамгийн багадаа 3 тэмдэгт байна.')
    // Checked before the catch-all below, which would otherwise answer a
    // perfectly legal set of characters with "only latin letters, digits and
    // hyphens" - true, but not the reason this name was refused.
    if(name.startsWith('-')) return setHint('Домэйн нэр зурааснаас эхэлж болохгүй.')
    if(name.endsWith('-')) return setHint('Домэйн нэр зураасаар төгсөж болохгүй.')
    if(!VALID.test(name)) return setHint('Зөвхөн латин үсэг, тоо, дундуур зураас ашиглана.')

    setHint(null)
    setResultPeriod(1)
    setOtherResults([])
    startTransition(async () => {
      // `/public/domain/search` is deliberately public: this page is the main
      // funnel and is reachable signed out, so gating the search would mean
      // anonymous visitors only ever saw `.mn` and never discovered the other
      // TLDs. Buying still requires a session - that gate lives on the cart.
      //
      // `results[0]` is always the `.mn` row; the backend guarantees the order.
      // It drives the hero card and the backdrop glow, so the headline verdict
      // never flickers on a `.com` result.
      await runSearch(name, 1)
    })
  }

  /**
   * One fetch for the whole page, at one term.
   *
   * The term goes to the server rather than multiplying a 1-year price on the
   * client: registries commonly price the first year promotionally (.xyz is
   * about $1 year one and $12 to renew), so `price * years` would quote a
   * multi-year registration at a fraction of its real cost. The backend holds a
   * row per (tld, duration) for exactly this reason.
   */
  const runSearch = async (name: string, term: number) => {
      setSearchedName(name)
      const res: any = await searchDomains(name, term)
      if(!res || res.error) return setResult({ kind: 'error', message: res?.error || 'Шалгах үед алдаа гарлаа' })
      const [mn, ...rest]: DomainSearchResult[] = res.results || []
      if(!mn) return setResult({ kind: 'error', message: 'Шалгах үед алдаа гарлаа' })
      setResult(
        mn.available
          // `sale` here means the FINAL discounted price, which is what the card
          // renders and strikes `price` through against. The API's `saleAmount`
          // is the DISCOUNT (amount - totalAmount), not the price — passing it
          // showed 65,000₮ on a name that charges 100,000₮.
          ? { kind: 'available', domain: mn.domain, price: mn.amount, sale: mn.totalAmount !== mn.amount ? mn.totalAmount : undefined, allowedYears: mn.allowedYears }
          : { kind: 'taken', domain: mn.domain }
      )
      setOtherResults(rest)
  }

  /** Re-prices every row at the new term. No effect hook: the select is the
   *  only thing that changes the term, so it owns the refetch. */
  const changePeriod = (next: number) => {
    setResultPeriod(next)
    if(!searchedName) return
    startTransition(async () => { await runSearch(searchedName, next) })
  }

  const submitIdea = (e: React.FormEvent) => {
    e.preventDefault()
    const text = idea.trim()

    if(text.length < IDEA_MIN) return setAiHint(`Хамгийн багадаа ${IDEA_MIN} тэмдэгт бичнэ үү.`)

    setAiHint(null)
    startAiTransition(async () => {
      const res: any = await suggestDomainNames(text, { tone, length })
      // Covers the rate limit too: the backend answers 429 with a message in the
      // same field as any other refusal, so it needs no separate branch here.
      if(!res || res.error) { setAi(null); return setAiHint(res?.error || 'Санал болгоход алдаа гарлаа') }
      setAi({ suggestions: res.suggestions || [], price: res.sale || res.price })
    })
  }

  const switchMode = (next: Mode) => {
    if(next === mode) return
    setMode(next)
    setHint(null)
    setAiHint(null)
  }

  const resultPrice = result?.kind === 'available' ? (result.sale || result.price) : undefined
  // resultPrice collapses to the sale when there is one, which would silently
  // drop the original. Kept so the card can strike it through.
  const onSale = result?.kind === 'available' && !!result.sale && !!result.price && result.sale !== result.price

  // The backdrop is not decoration: it carries the answer. Blue while you are
  // still asking, green when the name is free, grey when it is gone.
  //
  // AI mode answers with a list rather than a verdict, so it goes green when at
  // least one suggestion is actually buyable - which is the only outcome that
  // makes the list worth reading.
  const glow =
    mode === 'ai'
      ? aiHint ? 'var(--warning)'
        : ai ? (ai.suggestions.some(s => s.available) ? 'var(--success)' : 'var(--muted-foreground)')
        : 'var(--blue)'
    : result?.kind === 'available' ? 'var(--success)'
    : result?.kind === 'taken' ? 'var(--muted-foreground)'
    : result?.kind === 'error' ? 'var(--warning)'
    : 'var(--blue)'

  return (
    <>
      <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
        {/* Dot field, masked to concentrate behind the search and dissolve at the
            edges. Same device the Shop.mn vendor panel uses on its DAN card, so
            this reads as family rather than as invented ornament. */}
        <div
          className="absolute inset-0 opacity-40 dark:opacity-25"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)',
            backgroundSize: '22px 22px',
            color: 'var(--color-muted-foreground)',
            maskImage: 'radial-gradient(ellipse 60% 50% at 50% 45%, black 10%, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(ellipse 60% 50% at 50% 45%, black 10%, transparent 75%)',
          }}
        />
        <div
          className="absolute left-1/2 top-[45%] -translate-x-1/2 -translate-y-1/2 w-[min(120vw,900px)] aspect-square transition-[background] duration-200 motion-reduce:transition-none"
          style={{ background: `radial-gradient(circle, color-mix(in oklch, ${glow} 22%, transparent) 0%, transparent 65%)` }}
        />
      </div>

      {/* `my-auto` rather than `items-center` on the parent: a centred flex child
          that outgrows its container overflows equally top and bottom, so once the
          TLD list makes this taller than the viewport the headline gets pinned
          against the header with nowhere to scroll back to. Auto margins centre
          the same way while there is room and simply yield to the top edge when
          there is not. */}
      <div className="relative z-10 w-full max-w-2xl mx-auto my-auto">
        {/* text-balance keeps the two lines close in length instead of leaving one
            orphaned word on the second row. */}
        <h1 className="text-center text-balance font-gilroy font-semibold tracking-tight text-4xl sm:text-5xl lg:text-6xl">
          Домэйн нэрээ олсон уу?
        </h1>
        <p className="mt-4 text-center text-base sm:text-lg text-muted-foreground">
          { mode === 'search' ? 'Нэрээ олчихвол бусад нь амархан.' : 'Бизнесээ тайлбарлавал тохирох нэр санал болгоно.' }
        </p>

        {/* aria-pressed rather than a tablist: proper tabs owe the reader arrow-key
            navigation and a labelled panel, and this is two buttons that swap one
            field. The track is the same capsule the app shell's nav uses. */}
        <div className="mt-7 flex justify-center">
          {/* Translucent over the backdrop rather than sitting on top of it, so
              the status wash reads through the track: blue while you are asking,
              green once a name comes back free.

              The selected pill is translucent too, but much denser than the
              track. That gap is what separates them - both let the glow through,
              and the pill is simply the more solid of the two, with shadow-sm
              doing the rest. Narrow the gap and the selected tab stops being
              legible as selected. */}
          <div className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background/20 backdrop-blur-md p-1">
            { ([
              { id: 'search', label: 'Нэр шалгах', icon: Search },
              { id: 'ai', label: 'AI санал', icon: Sparkles },
            ] as const).map(tab => (
              <button
                key={tab.id}
                type="button"
                aria-pressed={mode === tab.id}
                onClick={() => switchMode(tab.id)}
                className={`inline-flex items-center gap-2 h-9 px-4 rounded-full text-sm font-medium cursor-pointer transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  mode === tab.id ? 'bg-back/75 text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <tab.icon className="size-4 shrink-0" aria-hidden="true" />
                {tab.label}
              </button>
            )) }
          </div>
        </div>

        { mode === 'search' ? (
        <form onSubmit={submit} className="mt-5">
          {/* The capsule is the control. `.mn` sits inside it as a fixed anchor
              rather than something you type, because there is only one TLD here - that is the difference between this and a generic domain search. */}
          <div
            onClick={() => inputRef.current?.focus()}
            // --glow is the same value the backdrop uses, so the field itself
            // turns green on a free name and amber on an error - the halo is
            // reporting the answer rather than decorating the box.
            style={{ '--glow': glow } as React.CSSProperties}
            className="search-glow flex items-center gap-2 sm:gap-3 h-14 sm:h-16 pl-4 sm:pl-5 pr-1.5 sm:pr-2 rounded-full cursor-text bg-back transition-shadow duration-200 motion-reduce:transition-none"
          >
            <Search className="size-5 text-muted-foreground shrink-0" aria-hidden="true" />
            <label htmlFor="domain" className="sr-only">Домэйн нэр</label>
            <input
              ref={inputRef}
              id="domain"
              name="domain"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              autoFocus
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              enterKeyHint="search"
              maxLength={63}
              placeholder={PLACEHOLDER}
              aria-describedby="domain-hint"
              // appearance-none strips the browser's native input chrome, which
              // otherwise draws its own box inside the capsule.
              className="min-w-0 flex-1 appearance-none border-0 bg-transparent shadow-none outline-none focus:outline-none focus:ring-0 text-base sm:text-lg tracking-tight placeholder:text-muted-foreground/50"
            />
            <span aria-hidden="true" className="font-medium text-base sm:text-lg text-blue shrink-0">.mn</span>
            {/* size="lg" brings px-8, which on a phone leaves the button wider
                than the space left for what you are typing. Tightened below sm;
                ! because the size variant sets padding at the same specificity. */}
            <Button type="submit" size="lg" className="rounded-full! shrink-0 px-5! sm:px-8!" isLoading={pending} loadingMessage="Шалгаж байна" isActive={value.length > 0}>
              Шалгах
            </Button>
          </div>

          <p id="domain-hint" className={`mt-3 text-center text-sm ${hint ? 'text-warning' : 'text-muted-foreground'}`}>
            {hint || 'Латин үсэг, тоо, зураас. Хамгийн багадаа 3 тэмдэгт.'}
          </p>

          {/* `.mn` keeps its own pill and leads: it is the default offer and the
              overwhelming majority of demand, so the others trail it rather than
              sitting as equals. Wraps on a phone instead of scrolling sideways. */}
          { price && !result && (
            <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full border border-border bg-back px-3.5 py-1.5 text-sm">
                <span className="font-medium text-blue">.mn</span>
                <span className="text-muted-foreground">-</span>
                <span className="font-medium">{formatPrice(price)}</span>
              </span>

              { featured.map(item => (
                <span key={item.tld} className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-back/60 px-3 py-1.5 text-xs text-muted-foreground">
                  <span className="font-medium text-blue/80">.{item.tld}</span>
                  <span>-</span>
                  <span className="font-medium text-foreground/90">{formatPrice(item.price)}</span>
                </span>
              )) }
            </div>
          ) }
        </form>
        ) : (
        <form onSubmit={submitIdea} className="mt-5">
          {/* The capsule cannot hold three lines, so AI mode keeps the glow and
              the surface and loosens the radius. Same object, more room - not a
              different control. */}
          <div
            // Only the panel's own padding focuses the field. Without the
            // target check, clicking a tone chip would hand focus straight back
            // to the textarea - which for a keyboard user means the selection
            // they just made throws them out of the row they were in.
            onClick={(e) => { if(e.target === e.currentTarget) ideaRef.current?.focus() }}
            style={{ '--glow': glow } as React.CSSProperties}
            className="search-glow rounded-3xl bg-back p-3 sm:p-4 cursor-text transition-shadow duration-200 motion-reduce:transition-none"
          >
            <label htmlFor="idea" className="sr-only">Бизнесийн тайлбар</label>
            <textarea
              ref={ideaRef}
              id="idea"
              name="idea"
              value={idea}
              onChange={(e) => { setIdea(e.target.value); if(aiHint) setAiHint(null) }}
              // Enter inserts a newline in a textarea, so it cannot submit the
              // way the single-line field does. The usual shortcut stands in.
              onKeyDown={(e) => { if(e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submitIdea(e) }}
              autoFocus
              rows={3}
              maxLength={IDEA_MAX}
              placeholder="Жишээ нь: Улаанбаатарт гар аргаар шоколад хийж, онлайнаар зардаг жижиг бизнес."
              aria-describedby="idea-hint"
              className="w-full resize-none appearance-none border-0 bg-transparent shadow-none outline-none focus:outline-none focus:ring-0 text-base sm:text-lg tracking-tight placeholder:text-muted-foreground/50"
            />
            {/* One row with the submit, not a block stacked above it. These
                refine the request the button sends, so they sit on its line.
                Below sm the two dropdowns already fill the width, so the row
                wraps and the button takes the line underneath. */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 pt-2">
              <AiOptions tone={tone} length={length} onTone={setTone} onLength={setLength} />

              <div className="hidden sm:block sm:flex-1" />

              <div className="flex items-center gap-3 w-full sm:w-auto">
                {/* Only once it is close to the ceiling. A counter from the first
                    keystroke reads as a warning about a limit nobody is near. */}
                <p className="text-xs text-muted-foreground tabular-nums">
                  { idea.length > IDEA_MAX - 100 ? `${idea.length}/${IDEA_MAX}` : '' }
                </p>
                <Button
                  type="submit"
                  variant="ai"
                  size="lg"
                  className="rounded-full! px-5! sm:px-8! flex-1 sm:flex-none"
                  isLoading={aiPending}
                  loadingMessage="Бодож байна"
                  isActive={idea.trim().length >= IDEA_MIN}
                >
                  Санал авах
                </Button>
              </div>
            </div>
          </div>

          <p id="idea-hint" className={`mt-3 text-center text-sm ${aiHint ? 'text-warning' : 'text-muted-foreground'}`}>
            {aiHint || 'Юу зардаг, хэнд зориулсан бизнес болохоо 1-2 өгүүлбэрээр бичнэ үү.'}
          </p>
        </form>
        ) }

        {/* One live region for every verdict, so a screen reader announces the
            answer without focus being stolen from the input. The reserved height
            is the height of a result card - without it the page jumps on the first
            search, right where the eye already is. */}
        <div aria-live="polite" className={`mt-8 ${mode === 'search' ? 'min-h-[5.5rem]' : ''}`}>
          { mode === 'search' ? (
            // mode="wait" so one verdict finishes leaving before the next arrives -
            // overlapping them in a fixed-height slot would stack two cards.
            <AnimatePresence mode="wait" initial={false}>
              { result?.kind === 'available' && (
                // Tinted rather than neutral, using the same state-card idiom as the
                // dashboard's verification notice. The page already turns green on a
                // free name; this was the one element not joining in.
                //
                // Two rows, not one. Verdict and price on top, controls beneath:
                // a single row had to fit a name, a status, two prices, a select
                // and two buttons, which squeezed the name column until domains
                // broke across lines mid-word.
                <motion.div
                  key="available"
                  {...cardMotion}
                  className="rounded-xl border border-success/30 bg-success/10 p-4 sm:p-5 flex flex-col gap-4"
                >
                  <div className="flex items-start justify-between gap-4">
                    {/* No tick. The card is tinted green, the line below is green
                        and the whole page glows green - an icon saying "yes" a
                        fourth time only pushed the name, which is the actual
                        answer, further from the edge it is read from. The verdict
                        is still stated in words, so nothing here rests on colour. */}
                    <div className="min-w-0">
                      <p className="font-gilroy font-semibold text-lg tracking-tight break-all">{result.domain}</p>
                      <p className="text-sm text-success mt-0.5">Авах боломжтой</p>
                    </div>

                    { resultPrice ? (
                      // Stacked so `жилийн` sits under the price it describes. Side
                      // by side it landed under the struck-through original, which
                      // labelled the wrong number.
                      <div className="shrink-0 text-right">
                        { onSale ? (
                          <p className="text-sm text-muted-foreground line-through leading-tight">
                            {formatPrice(result.price || 0)}
                          </p>
                        ) : null }
                        {/* Bigger from sm up, where there is room. The struck-through
                            original stays small so the gap between them reads as
                            hierarchy rather than two competing numbers. */}
                        <p className="font-gilroy font-semibold text-lg sm:text-2xl tracking-tight leading-tight">
                          {formatPrice(resultPrice)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          { resultPeriod === 1 ? 'жилийн' : `${resultPeriod} жилийн` }
                        </p>
                      </div>
                    ) : null }
                  </div>

                  {/* Collect sits hard left, buy hard right, with the spacer
                      between them: they are opposite intentions, so pinning them
                      to opposite ends stops the eye reading them as one pair of
                      equal choices.

                      Below sm the collect button drops its label and keeps only
                      the basket icon, which frees the rest of the line for the
                      primary — most visitors want one name, and on a 320px phone
                      that action deserves the width rather than splitting it with
                      a detour. The label is still announced: `sr-only` keeps it in
                      the accessibility tree where `hidden` would remove it. */}
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Hidden when the cap leaves exactly one term — a select
                        with a single option is noise. Comes back on its own if
                        CART_MAX_PERIOD is raised. */}
                    { periodItems(result.allowedYears).length > 1 ? (
                      <div className="w-full sm:w-28 sm:flex-none">
                        <Select
                          value={String(resultPeriod)}
                          onChange={(v) => changePeriod(parseInt(v))}
                          items={periodItems(result.allowedYears)}
                        />
                      </div>
                    ) : null }

                    { cart.has(result.domain) ? (
                      <Button tag="button" variant="outline" className="w-full sm:w-auto" isActive={false}>
                        <Check className="size-5" aria-hidden="true" />
                        Сагсанд байна
                      </Button>
                    ) : cart.count === 0 ? (
                      <>
                        {/* Collect, for the minority buying several. It carries the
                            basket icon the header uses, so the button names where
                            the name is going - a bare plus said "add" without ever
                            saying add to what. The word rides along at every width:
                            with the primary on its own row below sm, there is room
                            for it beside the term, and no icon is left unlabelled. */}
                        <Button
                          tag="button"
                          variant="outline"
                          className="shrink-0"
                          title="Сагсанд нэмэх"
                          onClick={() => addToCart(result.domain)}
                        >
                          <ShoppingCart className="size-5" aria-hidden="true" />
                          <span className="sr-only sm:not-sr-only">Сагсанд нэмэх</span>
                        </Button>

                        <div className="hidden sm:block sm:flex-1" />

                        {/* Most people are here for one name, so the basket is a
                            detour. This skips it and goes straight to payment. */}
                        <Button
                          tag="button"
                          className="flex-1 sm:flex-none sm:w-auto"
                          isLoading={navPending}
                          loadingMessage="Түр хүлээнэ үү"
                          onClick={() => {
                            const allowedYears = result.allowedYears ?? null
                            // The name goes into the basket either way, so
                            // /checkout reads one source of truth and the
                            // basket survives any detour through login or ДАН
                            // (it lives in localStorage).
                            const res = cart.add(result.domain, resultPeriod, allowedYears)
                            // A full basket is the one case that must not
                            // navigate: the name was not saved, so checkout
                            // would open without it.
                            if (!res.ok && res.reason === 'full') return toast.error(`Сагсанд хамгийн ихдээ ${CART_MAX_ITEMS} домэйн нэр багтана`)
                            const blocker = checkoutBlocker({ signedIn, danVerified })
                            startNavTransition(() => { router.push(blocker || '/checkout') })
                          }}
                        >
                          Худалдаж авах
                          <ArrowRight className="size-5" aria-hidden="true" />
                        </Button>
                      </>
                    ) : (
                      // Once the basket holds something they are clearly collecting,
                      // so buying this one name alone would strand the rest and
                      // adding becomes the primary action. Nothing sits beside it
                      // now: the icon button that used to had the same handler and
                      // the same label, which is half of why a lone plus read as
                      // meaningless here.
                      <Button tag="button" className="w-full sm:w-auto" onClick={() => addToCart(result.domain)}>
                        <ShoppingCart className="size-5" aria-hidden="true" />
                        Сагсанд нэмэх
                      </Button>
                    ) }
                  </div>
                </motion.div>
              ) }

              { result?.kind === 'taken' && (
                // Same shape as the available card so the two verdicts read as one
                // set, in the muted tone the backdrop already uses for a taken name.
                <motion.div
                  key="taken"
                  {...cardMotion}
                  className="rounded-xl border border-border bg-muted/40 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5"
                >
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <CircleSlash className="size-5 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
                    <div className="min-w-0">
                      <p className="font-gilroy font-semibold text-lg tracking-tight break-all text-muted-foreground">{result.domain}</p>
                      <p className="text-sm text-muted-foreground mt-0.5">Энэ нэр бүртгэлтэй байна</p>
                    </div>
                  </div>
                  {/* The card used to end in a dead half. This is the only useful
                      thing to do with a taken name, so it goes where the available
                      card puts its call to action. */}
                  <Button tag="button" variant="outline" onClick={reset} className="shrink-0">
                    <RotateCcw className="size-5" aria-hidden="true" />
                    Өөр нэр оруулах
                  </Button>
                </motion.div>
              ) }

              { result?.kind === 'error' && (
                <motion.div
                  key="error"
                  {...cardMotion}
                  className="rounded-xl border border-warning/30 bg-warning/10 p-4 sm:p-5 flex items-start gap-3"
                >
                  <TriangleAlert className="size-5 text-warning shrink-0 mt-0.5" aria-hidden="true" />
                  <p className="text-sm">{result.message}</p>
                </motion.div>
              ) }
            </AnimatePresence>
          ) : ai ? (
            // No AnimatePresence here. The list is not a verdict swapping for
            // another verdict in a fixed slot - it appears once per request and
            // is replaced wholesale, so it animates itself on mount.
            <AiSuggestions suggestions={ai.suggestions} price={ai.price} signedIn={signedIn} danVerified={danVerified} />
          ) : null }
        </div>

        { mode === 'search' && otherResults.length > 0 ? (
          // Outside the reserved-height slot above: these rows never take part
          // in its AnimatePresence swap, so they should not share its fixed
          // height either. Never rendered when the backend has no other TLDs
          // enabled - see the fallback note in `submit`.
          <div className="mt-6">
            <TldResultList options={otherResults} period={resultPeriod} signedIn={signedIn} danVerified={danVerified} />
          </div>
        ) : null }

        <CartPanel signedIn={signedIn} danVerified={danVerified} price={price} />
      </div>
    </>
  )
}
